import {
    Component,
    OnInit,
    QueryList,
    ViewChildren,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { BlockComponent } from "@components/container-block/block.component";
import { PageComponent } from "@components/page/page.component";
import { SaveButtonComponent } from "@components/save-button/save-button.component";

import { Observable } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ToastService } from "~app/common/components/toast/toast.service";
import { ConfirmDialogResponse, Mottagningsplatstyper } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { DirtyFormService } from "~app/common/services/dirty-form.service";
import { FeatureService } from "~app/common/services/feature.service";
import { MottagningsplatsDetaljerTexts } from "~app/features/forsaljning/mottagningsplats/mottagningsplats-detaljer/mottagningsplats-detaljer.texts";
import { ILagerplats, IMottagningsplats, Mottagningsplatstyp } from "~app/features/forsaljning/mottagningsplats/mottagningsplats.models";
import { MottagningsplatsService } from "../mottagningsplats.service";
import { MottagningsplatsDetaljerHeaderSvComponent } from "./mottagningsplats-detaljer-header-sv/mottagningsplats-detaljer-header-sv.component";
import { MottagningsplatsDetaljerHeaderComponent } from "./mottagningsplats-detaljer-header/mottagningsplats-detaljer-header.component";
import { MottagningsplatsDetaljerPlatsComponent } from "./mottagningsplats-detaljer-plats/mottagningsplats-detaljer-plats.component";

@Component({
    selector: "rask-mottagningsplats-detaljer",
    templateUrl: "./mottagningsplats-detaljer.component.html",
    styleUrls: ["./mottagningsplats-detaljer.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        PageComponent,
        ActionButtonComponent,
        MottagningsplatsDetaljerHeaderComponent,
        MottagningsplatsDetaljerHeaderSvComponent,
        BlockComponent,
        MottagningsplatsDetaljerPlatsComponent,
        SaveButtonComponent
    ]
})
export class MottagningsplatsDetaljerComponent implements OnInit {
    form: UntypedFormGroup;
    initialized = false;
    isNew = false;
    lagerplatser: ILagerplats[] = [];
    mottagningsplats: IMottagningsplats;
    mottagningsplatsTyper: string[];
    @ViewChildren("platser") platser: QueryList<MottagningsplatsDetaljerPlatsComponent>;
    texts = MottagningsplatsDetaljerTexts.texts;
    validForSave = false;

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly dirtyForm = inject(DirtyFormService);
    private readonly mottagningsplatsService = inject(MottagningsplatsService);
    private readonly raskFeatureService = inject(FeatureService);
    private readonly raskModalService = inject(RaskModalService);
    private readonly router = inject(Router);
    private readonly toastService = inject(ToastService);

    private isUtlandsplats: boolean;

    constructor() {

        this.mottagningsplatsTyper = Mottagningsplatstyper.Alla;
        this.mottagningsplatsService.fetchMottagningsplatsBasdata();
    }

    get canAddAvlastningsplats(): boolean {
        return this.mottagningsplats?.lagerplatser?.length < 10;
    }

    addAvlastningsplats(): void {
        if (this.mottagningsplats?.lagerplatser?.length >= 10) {
            return;
        }

        const valid = [
            "0",
            "1",
            "2",
            "3",
            "4",
            "5",
            "6",
            "7",
            "8",
            "9"
        ];
        const taken = this.mottagningsplats.lagerplatser.map(x => x.avlamningsplats);
        this.mottagningsplats.lagerplatser.push({
            avlamningsplats: valid.find(x => !taken.includes(x)),
            namn: this.mottagningsplats.namn,
            koordinatN: this.mottagningsplats.northing,
            koordinatE: this.mottagningsplats.easting,
            finnsIViol3: false,
            aktiv: true
        });

        this.form.markAsDirty();
    }

    canEdit(): boolean {
        return this.authenticationService.canEdit();
    }

    isSvenskMottagningsplats(): boolean {
        return this.getMottagningsplatsTyp() === Mottagningsplatstyp.svensk;
    }

    lagerplatserChanged(lagerplatser: ILagerplats[]) {
        this.mottagningsplats.lagerplatser.splice(0, this.mottagningsplats.lagerplatser.length);
        this.mottagningsplats.lagerplatser.push(...lagerplatser);
    }

    ngOnInit(): void {
        const id = this.activatedRoute.snapshot.paramMap.get("id");
        if (id !== "ny") {
            this.mottagningsplatsService
                .getMottagningsplatsById(+id)
                .subscribe((x) => {
                    this.mottagningsplats = x;
                    this.lagerplatser = this.mottagningsplats.lagerplatser;
                    this.isUtlandsplats = !!x.land;
                    this.mapFromModel();
                    this.syncUriState();
                    this.initialized = true;
                });
        } else {
            this.isNew = true;
            this.mottagningsplats = this.mottagningsplatsService.createEmptyMottagningsplats(
                this.isSvenskMottagningsplats()
            );
            this.mapFromModel();
            this.initialized = true;
        }

        this.dirtyForm.onDirtyNgModel(
            this.isModalDirty.bind(this),
            this.save.bind(this),
            null,
            () => this.validForSave,
            this.texts.lamnaHeader,
            this.texts.lamnaBody,
            {
                size: "lg",
                centered: false
            });
    }

    save(): void {
        if (this.form.valid) {
            const worker: Observable<IMottagningsplats> = this.busyService.addWork(
                this.mottagningsplatsService.saveMottagningsplats(
                    this.mottagningsplats
                )
            );
            worker.subscribe((result) => {
                this.isNew = false;
                this.isUtlandsplats = !!result.land;
                this.mottagningsplats = result;
                this.form.markAsPristine();
            });
        }
    }

    tabort(): void {
        this.raskModalService
            .confirm(new ModalSettings(this.texts.deleteHeader,this.texts.deleteText))
            .subscribe((response) => {
                if (response === ConfirmDialogResponse.save) {
                    const worker = this.busyService.addWork(
                        this.mottagningsplatsService.deleteMottagningsplatsById(
                            {
                                id: this.mottagningsplats.id,
                                rowVersion: this.mottagningsplats.rowVersion
                            }
                        )
                    );
                    worker.subscribe((result) => {
                        this.toastService.add(this.texts.deleteConfirmed);
                        this.router.navigate(["/mottagningsplats"]);
                    });
                }
            });
    }

    private getMottagningsplatsTyp(): string {
        if (this.isNew) {
            return this.getUriState();
        } else {
            if (!this.mottagningsplats) {
                // Innan vi fått svar så räknar vi fortfarande som Normal
                return Mottagningsplatstyp.svensk;
            } else {
                return this.isUtlandsplats ? Mottagningsplatstyp.utlandsk : Mottagningsplatstyp.svensk;
            }
        }
    }

    private getUriState(): string {
        const typ = this.activatedRoute.snapshot.paramMap.get("typ");
        if (typ === Mottagningsplatstyp.utlandsk) {
            return Mottagningsplatstyp.utlandsk;
        } else {
            return Mottagningsplatstyp.svensk;
        }
    }

    private isModalDirty(): boolean {
        return this.form.dirty;
    }

    private mapFromModel(): void {
        this.form = new UntypedFormGroup({});
        this.form.markAsPristine();
        this.form.valueChanges.subscribe(this.onFormChanges);
    }

    private readonly onFormChanges = (): void => {
        if (this.form.dirty && this.form.valid) {
            this.validForSave = true;
        } else {
            this.validForSave = false;
        }
    };

    private syncUriState(): void {
        // Eftersom användaren kan ändra ID från ett utlands-id till ett svenskt id
        // så kan vi synka upp adressen efter att vi hämtat objektet.
        const id = +this.activatedRoute.snapshot.paramMap.get("id");
        if (this.getMottagningsplatsTyp() !== this.getUriState()) {
            const mottagninsplatsUrl = "/mottagningsplats";
            if (
                this.getMottagningsplatsTyp() === Mottagningsplatstyp.utlandsk
            ) {
                this.router.navigate([
                    mottagninsplatsUrl,
                    Mottagningsplatstyp.utlandsk,
                    id
                ], {
                    replaceUrl: true
                });
            } else {
                this.router.navigate([
                    mottagninsplatsUrl,
                    Mottagningsplatstyp.svensk,
                    id
                ], {
                    replaceUrl: true
                });
            }
        }
    }
}
